/**
 * FlexDropdownColumnModule - Flex Coding Dropdown Columns for UniversalTransactionsTable
 *
 * ARCHITECTURE:
 * - renderFlexDropdown is the single source of truth for all dropdown rendering
 * - Two display configurations: TanStack table columns and sidesheet form
 * - Every cell resolves the edit predicate, readOnly columns included, because
 *   it needs hooks and so cannot sit behind a branch; the interactive renderer
 *   then calls its own hooks once
 */

import { Box } from '@mui/material';
import type { ColumnDef, CellContext } from '@tanstack/react-table';

import { HighnoteTransactionEvent } from 'API';
import { useAppSelector } from 'app/store';
import { selectTransactionById } from 'app/pages/store/transactionsSlice';
import { useFlexClientUtils } from 'app/pages/overview/tabs/transactions/FlexTransactions/utils';
import { FlexcodeStatusChip } from 'app/pages/overview/tabs/transactions/FlexTransactions/FlexStatusChip';
import {
  ReturnedStatusChip,
  shouldShowReturnedStatus,
} from 'app/pages/overview/tabs/transactions/ReturnedStatusChip';
import { useUserRole } from 'app/hooks/useUserRole';
import { useCanEditTransaction } from 'app/hooks/transactions/useCanEditTransaction';
import FlexGlCodeDropdown from 'app/pages/overview/tabs/transactions/FlexTransactions/FlexDropdowns/FlexGlCodeDropdown';
import FlexProjectDropdown from 'app/pages/overview/tabs/transactions/FlexTransactions/FlexDropdowns/FlexProjectDropdown';
import FlexCostCodeDropdown from 'app/pages/overview/tabs/transactions/FlexTransactions/FlexDropdowns/FlexCostCodeDropdown';
import FlexCostTypeDropdown from 'app/pages/overview/tabs/transactions/FlexTransactions/FlexDropdowns/FlexCostTypeDropdown';
import FlexVendorDropdown from 'app/pages/overview/tabs/transactions/FlexTransactions/FlexDropdowns/FlexVendorDropdown';
import { CodingSidesheetGrid } from 'app/pages/overview/tabs/transactions/CodingSidesheetGrid';
import type { CodingColumnId } from 'app/util/columnVisibility';
import { asyncNoop } from 'app/util/columnVisibility';
import type {
  CodingEntityRow,
  DropdownColumnModule,
  ModuleColumnProps,
  ToggleableColumn,
} from 'app/pages/overview/tabs/transactions/UniversalTransactionsTable';

type FlexDropdownType = 'glCode' | 'project' | 'costCode' | 'costType' | 'vendor';

// ---------------------------------------------------------------------------
// Type-safe handler contract derived from useFlexClientUtils
// ---------------------------------------------------------------------------

type FlexUtils = ReturnType<typeof useFlexClientUtils>;
export type FlexSidesheetHandlers = Pick<
  FlexUtils,
  | 'handleUpdateFlexGlCode'
  | 'handleUpdateFlexCode'
  | 'handleUpdateFlexCostCode'
  | 'handleUpdateFlexCostType'
  | 'handleUpdateFlexVendor'
>;

const noopHandlers: FlexSidesheetHandlers = {
  handleUpdateFlexGlCode: asyncNoop,
  handleUpdateFlexCode: asyncNoop,
  handleUpdateFlexCostCode: asyncNoop,
  handleUpdateFlexCostType: asyncNoop,
  handleUpdateFlexVendor: asyncNoop,
};

// ---------------------------------------------------------------------------
// Single source of truth: renders any Flex dropdown by type
// ---------------------------------------------------------------------------

function renderFlexDropdown(
  type: FlexDropdownType,
  transaction: CodingEntityRow,
  disabled: boolean,
  handlers: FlexSidesheetHandlers
): React.ReactElement | null {
  const txn = transaction as HighnoteTransactionEvent;

  switch (type) {
    case 'glCode':
      return (
        <FlexGlCodeDropdown
          selectedCode={transaction.flexGlCode}
          onValueChange={(option) => handlers.handleUpdateFlexGlCode(option)}
          disabled={disabled}
          transaction={txn}
        />
      );
    case 'project':
      return (
        <FlexProjectDropdown
          selectedGlCode={transaction.flexGlCode}
          selectedProject={transaction.flexCode}
          onValueChange={(option) => handlers.handleUpdateFlexCode(option)}
          disableHeader
          disabled={disabled}
          transaction={txn}
        />
      );
    case 'costCode':
      return (
        <FlexCostCodeDropdown
          selectedGlCode={transaction.flexGlCode}
          selectedProject={transaction.flexCode}
          selectedCostCode={transaction.flexCostCode}
          onValueChange={(option) => handlers.handleUpdateFlexCostCode(option)}
          disableHeader
          disabled={disabled}
        />
      );
    case 'costType':
      return (
        <FlexCostTypeDropdown
          selectedGlCode={transaction.flexGlCode}
          selectedProject={transaction.flexCode}
          selectedCostType={transaction.flexCostType}
          onValueChange={(option) => handlers.handleUpdateFlexCostType(option)}
          disableHeader
          disabled={disabled}
        />
      );
    case 'vendor':
      return (
        <FlexVendorDropdown
          selectedGlCode={transaction.flexGlCode}
          selectedProject={transaction.flexCode}
          selectedVendor={transaction.flexVendor}
          onValueChange={(option) => handlers.handleUpdateFlexVendor(option)}
          disableHeader
          disabled={disabled}
        />
      );
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Table cell: read-only path (zero hooks)
// ---------------------------------------------------------------------------

function ReadOnlyFlexDropdown({ row, column }: CellContext<CodingEntityRow, unknown>) {
  const type = (column.columnDef.meta as { dropdownType: FlexDropdownType }).dropdownType;
  return renderFlexDropdown(type, row.original, true, noopHandlers);
}

// ---------------------------------------------------------------------------
// Table cell: interactive path (hooks called once, delegates to renderFlexDropdown)
// ---------------------------------------------------------------------------

function InteractiveFlexDropdown({ row, column }: CellContext<HighnoteTransactionEvent, unknown>) {
  const transaction = row.original;
  const freshTransaction =
    useAppSelector((state) => selectTransactionById(state, transaction.id)) || transaction;
  const canEdit = useCanEditTransaction('flex');
  const {
    handleUpdateFlexGlCode,
    handleUpdateFlexCode,
    handleUpdateFlexCostCode,
    handleUpdateFlexCostType,
    handleUpdateFlexVendor,
  } = useFlexClientUtils({ transaction });

  const disabled = !canEdit(freshTransaction);
  const type = (column.columnDef.meta as { dropdownType: FlexDropdownType }).dropdownType;

  return renderFlexDropdown(type, freshTransaction, disabled, {
    handleUpdateFlexGlCode,
    handleUpdateFlexCode,
    handleUpdateFlexCostCode,
    handleUpdateFlexCostType,
    handleUpdateFlexVendor,
  });
}

// ---------------------------------------------------------------------------
// Unified table cell: renders read-only when the row cannot be edited, which is
// also what skips the dropdowns' data fetching. readOnly short-circuits the
// predicate for the surfaces that render coding locked whatever the row says.
// ---------------------------------------------------------------------------

function FlexDropdownCell(ctx: CellContext<HighnoteTransactionEvent | CodingEntityRow, unknown>) {
  const readOnly = (ctx.column.columnDef.meta as { readOnly?: boolean })?.readOnly;
  const canEdit = useCanEditTransaction('flex');

  if (readOnly || !canEdit(ctx.row.original as HighnoteTransactionEvent)) {
    return <ReadOnlyFlexDropdown {...(ctx as CellContext<CodingEntityRow, unknown>)} />;
  }
  return <InteractiveFlexDropdown {...(ctx as CellContext<HighnoteTransactionEvent, unknown>)} />;
}

// ---------------------------------------------------------------------------
// Status cell (separate concern)
// ---------------------------------------------------------------------------

export function FlexStatusCell({ row }: CellContext<HighnoteTransactionEvent, unknown>) {
  const transaction = row.original;
  const freshTransaction =
    useAppSelector((state) => selectTransactionById(state, transaction.id)) || transaction;
  const { isCardholder } = useUserRole();

  return (
    <Box display="flex" alignItems="center" gap={1}>
      {shouldShowReturnedStatus(freshTransaction) ? (
        <ReturnedStatusChip returnedStatus={freshTransaction.returnedStatus} isCardholder={isCardholder} />
      ) : (
        <FlexcodeStatusChip
          status={freshTransaction.flexCodeTransactionStatus}
          workflowStatus={freshTransaction.workflowStatus}
          transaction={freshTransaction}
        />
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Column map and module export
// ---------------------------------------------------------------------------

interface FlexColumnConfig {
  accessorKey: string;
  dropdownType: FlexDropdownType;
  size: number;
}

export const FLEX_COLUMN_MAP: Record<string, FlexColumnConfig> = {
  glCode: { accessorKey: 'flexGlCode', dropdownType: 'glCode', size: 190 },
  project: { accessorKey: 'flexCode', dropdownType: 'project', size: 190 },
  costCode: { accessorKey: 'flexCostCode', dropdownType: 'costCode', size: 256 },
  costType: { accessorKey: 'flexCostType', dropdownType: 'costType', size: 150 },
  vendor: { accessorKey: 'flexVendor', dropdownType: 'vendor', size: 190 },
};

function getFlexDropdownColumns(props: ModuleColumnProps): ColumnDef<HighnoteTransactionEvent, unknown>[] {
  const { isVisible, getLabel, orderedColumnIds, readOnly } = props;

  return orderedColumnIds
    .filter((colId) => isVisible(colId) && colId in FLEX_COLUMN_MAP)
    .map((colId) => {
      const config = FLEX_COLUMN_MAP[colId];
      return {
        id: colId,
        accessorKey: config.accessorKey,
        header: getLabel(colId as CodingColumnId),
        cell: FlexDropdownCell as (
          props: CellContext<HighnoteTransactionEvent, unknown>
        ) => React.ReactElement | null,
        size: config.size,
        enableSorting: false,
        enableHiding: true,
        meta: { dropdownType: config.dropdownType, readOnly },
      };
    });
}

function getFlexToggleableColumns(): ToggleableColumn[] {
  return [
    { id: 'glCode', label: 'GL Code', defaultVisible: true },
    { id: 'project', label: 'Project', defaultVisible: true },
    { id: 'costCode', label: 'Cost Code', defaultVisible: true },
    { id: 'costType', label: 'Cost Type', defaultVisible: true },
    { id: 'vendor', label: 'Vendor', defaultVisible: true },
  ];
}

export const flexDropdownModule: DropdownColumnModule<HighnoteTransactionEvent> = {
  moduleId: 'flex',
  getColumns: getFlexDropdownColumns,
  getToggleableColumns: getFlexToggleableColumns,
  StatusCell: FlexStatusCell,
};

// ---------------------------------------------------------------------------
// Sidesheet display configuration -- pure controlled component
// ---------------------------------------------------------------------------

export interface FlexSidesheetFormProps {
  /** Current coding values (from a transaction, reimbursement, or local form state) */
  values: CodingEntityRow | undefined;
  /** Called when the user selects a value in any dropdown. Ignored if handlers is provided. */
  onChange?: (field: keyof CodingEntityRow, value: unknown) => void;
  /** Override handlers directly (e.g. for bulk coding with cascade logic). */
  handlers?: FlexSidesheetHandlers;
  disabled?: boolean;
  visibleColumnIds: CodingColumnId[];
  getLabel: (colId: CodingColumnId) => string;
}

function flexOnChangeToHandlers(
  onChange: (field: keyof CodingEntityRow, value: unknown) => void
): FlexSidesheetHandlers {
  return {
    handleUpdateFlexGlCode: async (v) => onChange('flexGlCode', v),
    handleUpdateFlexCode: async (v) => onChange('flexCode', v),
    handleUpdateFlexCostCode: async (v) => onChange('flexCostCode', v),
    handleUpdateFlexCostType: async (v) => onChange('flexCostType', v),
    handleUpdateFlexVendor: async (v) => onChange('flexVendor', v),
  };
}

export function FlexSidesheetForm({
  values,
  onChange,
  handlers: handlersProp,
  disabled = false,
  visibleColumnIds,
  getLabel,
}: FlexSidesheetFormProps) {
  if (!values) return null;

  const handlers = handlersProp ?? flexOnChangeToHandlers(onChange!);

  return (
    <CodingSidesheetGrid
      visibleColumnIds={visibleColumnIds}
      getLabel={getLabel}
      renderContent={(colId) => {
        if (!(colId in FLEX_COLUMN_MAP)) return null;
        return renderFlexDropdown(colId as FlexDropdownType, values, disabled, handlers);
      }}
    />
  );
}

export default flexDropdownModule;
